import React, { useEffect, useState } from "react";
import styles from "./PaymentModule.module.css";
import { Add, Refresh } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import { FaTrash } from "react-icons/fa";
import Select from "react-select";

const selectStyle1 = {
  control: (base) => ({
    ...base,
    height: "38px",
    width: "100%",
    minHeight: "32px",
    zIndex: "10",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#aaa",
    fontStyle: "italic",
    display: "none",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "blue"
      : state.isFocused
      ? "lightblue"
      : "white",
    color: state.isSelected ? "white" : "black",
    padding: "6px",
    fontSize: "12px",
    whiteSpace: "normal",
    textOverflow: "unset",
    overflow: "visible",
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: "12px",
    padding: "2px 2px 2px 0px",
    whiteSpace: "normal",
    textOverflow: "unset",
    overflow: "visible",
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "8px",
    marginTop: "5px",
    zIndex: 999,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: "100px",
    overflowY: "auto",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "4px",
    svg: {
      width: "12px",
      height: "12px",
    },
  }),
  indicatorsContainer: (base) => ({
    ...base,
    padding: "0px",
  }),
};

const paymentModes = [
  { value: "Cash", label: "Cash" },
  { value: "Card", label: "Card" },
  { value: "UPI", label: "UPI" },
  { value: "Cheque", label: "Cheque" },
  { value: "RTGS", label: "RTGS" },
  { value: "MDS", label: "MDS" },
  { value: "Metal", label: "Metal" },
  { value: "Metal to Cash", label: "Metal to Cash" },
  { value: "Cash to Metal", label: "Cash to Metal" },
];

const transactionTypes = [
  { value: "Invoice", label: "Invoice" },
  { value: "Order Advance", label: "Order Advance" },
  { value: "Repair", label: "Repair" },
  { value: "Return", label: "Return" },
  { value: "Credit Note", label: "Credit Note" },
  { value: "Other", label: "Other" },
];

const PaymentModule = React.memo(
  ({
    setPaymentsArray,
    setPaymentAmount,
    taxAmount = 0,
    fineMetal = 0,
    paymentsArray,
    paymentAmount,
    formData,
  }) => {
    const {
      register,
      handleSubmit,
      control,
      watch,
      setValue,
      reset,
      formState: { errors },
    } = useForm({
      defaultValues: {
        paymentType: "Received",
        paymentMode: "",
        paymentAmount: "",
        paymentDescription: "",
        metal: "",
        totalWeight: "",
        finePercentage: "",
        finePaid: "",
        ratePerGram: "",
        metalTotalAmount: "",
      },
    });

    const {
      register: amountRegister,
      handleSubmit: amountHandleSubmit,
      control: amountControl,
      watch: amountWatch,
      setValue: amountSetValue,
      reset: amountReset,
    } = useForm({
      defaultValues: paymentAmount,
    });

    const paymentMode = watch("paymentMode");
    const isGst = amountWatch("isGst");
    const taxableAmount = amountWatch("taxableAmount");
    const discount = amountWatch("discount");
    const gstAmount = amountWatch("gstAmount");
    const additionalTax = amountWatch("additionalTax");
    const isAdditionalTax = amountWatch("isAdditionalTax");
    const courierCharge = amountWatch("courierCharge");
    const paidAmount = amountWatch("paidAmount");
    const totalAmount = amountWatch("totalAmount");
    const totalWeight = watch("totalWeight");
    const finePercentage = watch("finePercentage");
    const finePaid = watch("finePaid");
    const ratePerGram = watch("ratePerGram");

    // useEffect(() => {
    //   if (payments?.length) {
    //     let totalTaxAmt = 0;
    //     payments?.forEach((item) => {
    //       totalTaxAmt += +item?.paymentAmount || 0;
    //     });
    //     amountSetValue("taxableAmount", ((+(taxAmount) || 0) + totalTaxAmt)?.toFixed(2));
    //   } else {
    //     amountSetValue("taxableAmount", "");
    //   }
    //   setPaymentsArray(payments)
    // }, [payments,taxAmount]);

    const [payments, setPayments] = useState(paymentsArray);

    useEffect(() => {
      amountSetValue("taxableAmount", (+taxAmount || 0).toFixed(2));
    }, [taxAmount]);

    useEffect(() => {
      const totalPaid = payments.reduce(
        (sum, payment) => sum + parseFloat(payment.paymentAmount || 0),
        0
      );
      amountSetValue("paidAmount", totalPaid.toFixed(2));
    }, [payments]);

    useEffect(() => {
      if (isGst) {
        amountSetValue(
          "gstAmount",
          (((+taxableAmount || 0) * 3) / 100)?.toFixed(2)
        );
      } else {
        amountSetValue("gstAmount", "");
      }
      if (isAdditionalTax) {
        amountSetValue(
          "additionalTax",
          (((+taxableAmount || 0) * 0.1) / 100)?.toFixed(2)
        );
      } else {
        amountSetValue("additionalTax", "");
      }
    }, [isGst, isAdditionalTax, taxableAmount]);

    // Update local payments state when paymentsArray prop changes
    useEffect(() => {
      setPayments(paymentsArray);
    }, [paymentsArray]);

    // Check if current payment mode is metal-related
    const isMetalPayment = ["Metal", "Metal to Cash", "Cash to Metal"].includes(
      paymentMode
    );

    // Get payment mode options based on GST
    const getPaymentModeOptions = () => {
      if (!isGst) {
        return [
          { value: "Cash", label: "Cash" },
          { value: "Metal", label: "Metal" },
          { value: "Metal to Cash", label: "Metal to Cash" },
          { value: "Cash to Metal", label: "Cash to Metal" },
        ];
      }
      return paymentModes;
    };

    // Add useEffect for calculations
    useEffect(() => {
      if (!isMetalPayment) return;

      switch (paymentMode) {
        case "Metal":
          // Calculate Fine Paid when Total Weight or Fine % changes
          if (totalWeight && finePercentage) {
            const calculatedFinePaid = totalWeight * (finePercentage / 100);
            setValue("finePaid", calculatedFinePaid.toFixed(3));
          }
          break;

        case "Metal to Cash":
          // Calculate Total Amount when Fine Paid or Rate/Gm changes
          if (finePaid && ratePerGram) {
            const calculatedTotalAmount = finePaid * ratePerGram;
            setValue("metalTotalAmount", calculatedTotalAmount.toFixed(3));
          }
          break;

        case "Cash to Metal":
          // Calculate Fine Paid when Total Amount or Rate/Gm changes
          if (watch("metalTotalAmount") && ratePerGram) {
            const calculatedFinePaid = watch("metalTotalAmount") / ratePerGram;
            setValue("finePaid", calculatedFinePaid.toFixed(3));
            // Also set the payment amount for the form submission
            setValue("paymentAmount", watch("metalTotalAmount"));
          }
          break;
      }
    }, [
      paymentMode,
      totalWeight,
      finePercentage,
      finePaid,
      ratePerGram,
      isMetalPayment,
      setValue,
      watch,
    ]);

    // Add new useEffect to reset form values when payment mode changes
    useEffect(() => {
      if (paymentMode) {
        // Reset all metal-related fields
        setValue("metal", "");
        setValue("totalWeight", "");
        setValue("finePercentage", "");
        setValue("finePaid", "");
        setValue("ratePerGram", "");
        setValue("metalTotalAmount", "");
        setValue("paymentAmount", "");
        setValue("paymentDescription", "");
      }
    }, [paymentMode, setValue]);

    useEffect(() => {
      if (payments?.length) {
        let totalPaidAmount = 0;
        let totalFinePaid = 0;
        let totalFinePaidGold = 0;
        let totalFinePaidSilver = 0;

        payments?.forEach((item) => {
          totalPaidAmount += +item?.paymentAmount || 0;

          // Calculate total fine paid for metal payments
          if (
            item?.paymentMode === "Metal" ||
            item?.paymentMode === "Metal to Cash" ||
            item?.paymentMode === "Cash to Metal"
          ) {
            totalFinePaid += +item?.finePaid || 0;
            if (item?.metal === "Gold") {
              totalFinePaidGold += +item?.finePaid || 0;
            } else if (item?.metal === "Silver") {
              totalFinePaidSilver += +item?.finePaid || 0;
            }
          }
        });

        amountSetValue("paidAmount", totalPaidAmount?.toFixed(2));
        amountSetValue("paidMetal", totalFinePaid.toFixed(3));

        // Calculate new balance amount
        const initialBalance = +formData.balanceAmount || 0;
        const newBalance = initialBalance - totalPaidAmount;
        amountSetValue("balanceAmount", newBalance.toFixed(2));

        // Update the payment table to show metal amounts
        setPayments(
          payments.map((item) => {
            if (
              item?.paymentMode === "Metal" ||
              item?.paymentMode === "Metal to Cash" ||
              item?.paymentMode === "Cash to Metal"
            ) {
              return {
                ...item,
                gold: item?.metal === "Gold" ? item?.finePaid : "",
                silver: item?.metal === "Silver" ? item?.finePaid : "",
              };
            }
            return item;
          })
        );
      } else {
        amountSetValue("paidAmount", "");
        amountSetValue("paidMetal", "0.000");
        // Reset balance amount to initial value when no payments
        amountSetValue("balanceAmount", formData.balanceAmount || "0");
      }
    }, [payments, amountSetValue, formData.balanceAmount]);

    useEffect(() => {
      if (isGst) {
        amountSetValue(
          "gstAmount",
          (((+taxableAmount || 0) * 3) / 100)?.toFixed(2)
        );
      } else {
        amountSetValue("gstAmount", "");
      }
      if (isAdditionalTax) {
        amountSetValue(
          "additionalTax",
          (((+taxableAmount || 0) * 0.1) / 100)?.toFixed(2)
        );
      } else {
        amountSetValue("additionalTax", "");
      }
    }, [isGst, isAdditionalTax, taxableAmount]);

    useEffect(() => {
      let sumAmount =
        (+taxableAmount || 0) +
        (+gstAmount || 0) +
        (+additionalTax || 0) -
        (+discount || 0) +
        (+courierCharge || 0);
      amountSetValue("totalAmount", sumAmount?.toFixed(2));
      // amountSetValue(
      //   "balanceAmount",
      //   (sumAmount - (+paidAmount || 0))?.toFixed(2)
      // );
    }, [
      taxableAmount,
      gstAmount,
      additionalTax,
      discount,
      courierCharge,
      paidAmount,
    ]);

    useEffect(() => {
      setPaymentAmount({
        taxableAmount,
        isGst,
        gstAmount,
        additionalTax,
        isAdditionalTax,
        discount,
        courierCharge,
        totalAmount,
        paidAmount,
        balanceMetal: amountWatch("balanceMetal") || "0",
        fineMetal: amountWatch("fineMetal") || "0",
        balanceAmount: amountWatch("balanceAmount") || "0",
      });
    }, [
      taxableAmount,
      gstAmount,
      isGst,
      additionalTax,
      isAdditionalTax,
      discount,
      courierCharge,
      paidAmount,
      totalAmount,
    ]);

    // Update delete handler to use setPaymentsArray directly
    const handleDelete = (index) => {
      setPaymentsArray((prevPayments) =>
        prevPayments.filter((_, idx) => idx !== index)
      );
    };

    const onSubmit = (data) => {
      // For metal payments, use the calculated values
      if (isMetalPayment) {
        if (paymentMode === "Cash to Metal") {
          data.paymentAmount = data.metalTotalAmount;
        } else {
          data.paymentAmount = data.metalTotalAmount || "0";
        }
        data.paymentDescription = "";
      }

      setPaymentsArray((prev) => [...prev, data]);
      reset({
        paymentType:
          watch("paymentType") === "Paid"
            ? "Paid"
            : watch("paymentType") === "Received"
            ? "Received"
            : "Advance",
        paymentMode: "",
        paymentAmount: "",
        paymentDescription: "",
        metal: "",
        totalWeight: "",
        finePercentage: "",
        finePaid: "",
        ratePerGram: "",
        metalTotalAmount: "",
      });
    };

    return (
      <div className={styles.container}>
        <div style={{ display: "flex", gap: "20px" }}>
          <div className={styles.formGroup} style={{ width: "12%" }}>
            <label style={{ marginBottom: "5px" }}>Transaction Type</label>
            <Select
              className={styles["summary-select"]}
              styles={selectStyle1}
              options={transactionTypes}
              // onChange={handleTransactionTypeChange}
            ></Select>
          </div>
          <div className={styles.formGroup} style={{ width: "12%" }}>
            <label style={{ marginBottom: "5px" }}>Transaction No.</label>
            <Select
              {...register("paymentMode")}
              className={styles["summary-select"]}
              styles={selectStyle1}
            >
              <option value="">-- select --</option>
              {paymentModes.map((item) => (
                <option key={item?.value} value={item?.value}>
                  {item?.label}
                </option>
              ))}
            </Select>
          </div>
          <div className={styles.formGroup}>
            <label style={{ marginBottom: "7px" }}>Description</label>
            <input
              {...register("paymentDescription")}
              style={{
                width: "157%",
                height: "35px",
                outline: "1px solid #ccc",
              }}
              className={styles["summary-input"]}
              type="text"
            />
          </div>
        </div>
        <div
          style={{
            display: "flex",
            paddingTop: "20px",
            justifyContent: "space-between",
          }}
        >
          <div className={styles.paymentSection}>
            <div className={styles.header}>
              <label style={{ display: "flex", gap: "15px", color: "#fff" }}>
                <input type="radio" value="Paid" {...register("paymentType")} />{" "}
                Paid
              </label>
              <label style={{ display: "flex", gap: "15px", color: "#fff" }}>
                <input
                  type="radio"
                  value="Received"
                  {...register("paymentType")}
                />{" "}
                Received
              </label>
              <label style={{ display: "flex", gap: "15px", color: "#fff" }}>
                <input
                  type="radio"
                  value="Advance"
                  {...register("paymentType")}
                />{" "}
                Advance
              </label>
            </div>
            <div className={styles["form-c"]}>
              <div style={{ display: "flex", gap: "10px" }}></div>
              <div style={{ display: "flex", gap: "10px" }}>
                <div className={styles.formGroup}>
                  <label>Payment Mode</label>
                  <select
                    {...register("paymentMode")}
                    className={styles["summary-select"]}
                  >
                    <option value="">-- select --</option>
                    {paymentModes.map((item) => (
                      <option key={item?.value} value={item?.value}>
                        {item?.label}
                      </option>
                    ))}
                  </select>
                </div>

                {(!isMetalPayment || isGst) && (
                  <>
                    <div className={styles.formGroup}>
                      <label>Amount</label>
                      <input
                        {...register("paymentAmount")}
                        style={{ width: "100%", height: "33px" }}
                        className={styles["summary-input"]}
                        type="number"
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Description</label>
                      <input
                        {...register("paymentDescription")}
                        style={{ width: "100%", height: "33px" }}
                        className={styles["summary-input"]}
                        type="text"
                      />
                    </div>
                  </>
                )}

                <div
                  style={{ display: "flex", gap: "10px", marginTop: "18px" }}
                >
                  <Add
                    onClick={handleSubmit(onSubmit)}
                    type="button"
                    style={{
                      cursor: "pointer",
                      fontSize: "30px",
                      background: "#0077d4",
                      color: "white",
                      border: "1px solid #0077d4",
                      borderRadius: "20%",
                    }}
                  />
                  <Refresh
                    type="button"
                    style={{
                      cursor: "pointer",
                      fontSize: "30px",
                      background: "white",
                      color: "#0077d4",
                      border: "1px solid #0077d4",
                      borderRadius: "20%",
                    }}
                  />
                </div>
              </div>
            </div>
            {isMetalPayment && !isGst && (
              <div className={styles.metalInputs}>
                <div className={styles.formGroup}>
                  <label>Metal</label>
                  <select
                    {...register("metal")}
                    className={styles["summary-select"]}
                  >
                    <option value="">-- select --</option>
                  </select>
                </div>

                {paymentMode === "Metal" && (
                  <>
                    <div className={styles.formGroup}>
                      <label>Total Weight</label>
                      <input
                        {...register("totalWeight")}
                        style={{ width: "100%", height: "33px" }}
                        className={styles["summary-input"]}
                        type="number"
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Fine %</label>
                      <input
                        {...register("finePercentage")}
                        style={{ width: "100%", height: "33px" }}
                        className={styles["summary-input"]}
                        type="number"
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Fine Paid</label>
                      <input
                        {...register("finePaid")}
                        style={{ width: "100%", height: "33px" }}
                        className={styles["summary-input"]}
                        type="number"
                        readOnly
                      />
                    </div>
                  </>
                )}

                {paymentMode === "Metal to Cash" && (
                  <>
                    <div className={styles.formGroup}>
                      <label>Fine Paid</label>
                      <input
                        {...register("finePaid")}
                        style={{ width: "100%", height: "33px" }}
                        className={styles["summary-input"]}
                        type="number"
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Rate/Gm</label>
                      <input
                        {...register("ratePerGram")}
                        style={{ width: "100%", height: "33px" }}
                        className={styles["summary-input"]}
                        type="number"
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Total Amount</label>
                      <input
                        {...register("metalTotalAmount")}
                        style={{ width: "100%", height: "33px" }}
                        className={styles["summary-input"]}
                        type="number"
                        readOnly
                      />
                    </div>
                  </>
                )}

                {paymentMode === "Cash to Metal" && (
                  <>
                    <div className={styles.formGroup}>
                      <label>Total Amount</label>
                      <input
                        {...register("metalTotalAmount")}
                        style={{ width: "100%", height: "33px" }}
                        className={styles["summary-input"]}
                        type="number"
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Rate/Gm</label>
                      <input
                        {...register("ratePerGram")}
                        style={{ width: "100%", height: "33px" }}
                        className={styles["summary-input"]}
                        type="number"
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Fine Paid</label>
                      <input
                        {...register("finePaid")}
                        style={{ width: "100%", height: "33px" }}
                        className={styles["summary-input"]}
                        type="number"
                        readOnly
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            <table className={styles.paymentTable}>
              <thead>
                <tr className={styles["table-header-row"]}>
                  <th
                    style={{
                      fontSize: "12px",
                      padding: "2px",
                      backgroundColor: "#627282",
                      color: "#fff",
                    }}
                  >
                    Sr No
                  </th>
                  <th
                    style={{
                      fontSize: "12px",
                      padding: "2px",
                      backgroundColor: "#627282",
                      color: "#fff",
                    }}
                  >
                    Mode
                  </th>
                  <th
                    style={{
                      fontSize: "12px",
                      padding: "2px",
                      backgroundColor: "#627282",
                      color: "#fff",
                    }}
                  >
                    Amount
                  </th>
                  <th
                    style={{
                      fontSize: "12px",
                      padding: "2px",
                      backgroundColor: "#627282",
                      color: "#fff",
                    }}
                  >
                    Gold
                  </th>
                  <th
                    style={{
                      fontSize: "12px",
                      padding: "2px",
                      backgroundColor: "#627282",
                      color: "#fff",
                    }}
                  >
                    Silver
                  </th>
                  <th
                    style={{
                      fontSize: "12px",
                      padding: "2px",
                      backgroundColor: "#627282",
                      color: "#fff",
                    }}
                  >
                    Description
                  </th>
                  <th
                    style={{
                      fontSize: "12px",
                      padding: "2px",
                      backgroundColor: "#627282",
                      color: "#fff",
                    }}
                  >
                    Delete
                  </th>
                </tr>
              </thead>
              <tbody>
                {payments?.length ? (
                  payments?.map((item, index) => (
                    <tr key={`payment-row-${index}`}>
                      <td>{index + 1}</td>
                      <td>{item?.paymentMode}</td>
                      <td>{item?.paymentAmount}</td>
                      <td>{item?.gold}</td>
                      <td>{item?.silver}</td>
                      <td title={item?.paymentDescription}></td>
                      <td>
                        <FaTrash
                          style={{ cursor: "pointer" }}
                          onClick={() => handleDelete(index)}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7">No Payments Added</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className={styles.summarySection}>
            <div className={styles.summaryLeft}>
              <div className={styles.summaryLeftField}>
                <label htmlFor="" style={{ fontWeight: "bold" }}>
                  Fine Metal
                </label>
                <input
                  type="text"
                  disabled={true}
                  className={styles.summaryInput}
                  {...amountRegister("fineMetal")}
                />
              </div>
              <div className={styles.summaryLeftField}>
                <label
                  htmlFor=""
                  style={{ fontWeight: "bold" }}
                  disabled={true}
                >
                  Paid Metal
                </label>
                <input
                  type="text"
                  className={styles.summaryInput}
                  disabled={true}
                  {...amountRegister("paidMetal")}
                />
              </div>
              <div className={styles.summaryLeftField}>
                <label htmlFor="" style={{ fontWeight: "bold" }}>
                  Balance Metal
                </label>
                <input
                  type="text"
                  disabled={true}
                  className={styles.summaryInput}
                  {...amountRegister("balanceMetal")}
                />
              </div>
              <div className={styles.summaryLeftField}>
                <label htmlFor="" style={{ fontWeight: "bold" }}>
                  Advance Metal
                </label>
                <input
                  type="text"
                  disabled={true}
                  className={styles.summaryInput}
                  {...amountRegister("advanceMetal")}
                />
              </div>
              {/* <div className={styles.summaryLeftField}>
            <label htmlFor="" style={{ fontWeight: "bold" }}>
              Courier Charge
            </label>
            <input
              type="text"
              className={styles.summaryInput}
              {...amountRegister("courierCharge")}
            />
          </div> */}
              {/* <div className={styles.summaryLeftField}>
            <label htmlFor="" style={{ fontWeight: "bold" }}>
              {watch("paymentType") === "Paid" ? "Paid Amount" : watch("paymentType") === "Received" ? "Received Amount" : "Advance Amount"}
            </label>
            <input
              type="text"
              disabled={true}
              className={styles.summaryInput}
              {...amountRegister("paidAmount")}
            />
          </div> */}
            </div>
            <div className={styles.summaryRight}>
              <div className={styles.summaryLeftField}>
                <label htmlFor="" style={{ fontWeight: "bold" }}>
                  Amount
                </label>
                <input
                  type="text"
                  className={styles.summaryInput}
                  disabled={true}
                  {...amountRegister("taxableAmount")}
                />
              </div>

              <div className={styles.summaryLeftField}>
                <label htmlFor="" style={{ fontWeight: "bold" }}>
                  Paid Amount
                </label>
                <input
                  type="text"
                  disabled={true}
                  className={styles.summaryInput}
                  {...amountRegister("gstAmount")}
                />
              </div>
              {/* <div className={styles.summaryLeftField}>
            <label htmlFor="" style={{ fontWeight: "bold" }}>
              TDS(0.1%)
            </label>
            <input
              type="checkbox"
             
              style={{ width: "48px", height: "15px" }}
              {...amountRegister("isAdditionalTax")}
            />
            <input
              type="text"
              disabled={true}
              className={styles.summaryInput}
              {...amountRegister("additionalTax")}
            />
          </div>
          <div className={styles.summaryLeftField}>
            <label htmlFor="" style={{ fontWeight: "bold" }}>
              R.O./Discount(-)
            </label>
            <input
              type="text"
              className={styles.summaryInput}
              {...amountRegister("discount")}
            />
          </div> */}

              <div className={styles.summaryLeftField}>
                <label htmlFor="" style={{ fontWeight: "bold" }}>
                  Balance Amount
                </label>
                <input
                  type="text"
                  disabled={true}
                  className={styles.summaryInput}
                  {...amountRegister("totalAmount")}
                />
              </div>
              <div className={styles.summaryLeftField}>
                <label htmlFor="" style={{ fontWeight: "bold" }}>
                  Advance Amount
                </label>
                <input
                  type="text"
                  disabled={true}
                  className={styles.summaryInput}
                  {...amountRegister("balanceAmount")}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default PaymentModule;
